import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, setToken } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Sparkles, Eye, EyeOff, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { AltioraTreeLogo } from "@/components/brand/AltioraTreeLogo";

/**
 * ALTIORA — Institution Beneficiary Onboarding (JoinFromInvite)
 * ---------------------------------------------------------------
 * Landing conversationnel pour les bénéficiaires invités par une institution.
 * Doctrine (§ ALTIORA_METHOD_IMES) : 1 idée par écran, jamais de formulaire brutal.
 *
 * URL : /join?invite=<invite_id>&org=<org_id>
 * Flow : 1. Salutation par l'institution → 2. Mot de passe → 3. Bienvenue → 4. Diagnostic.
 */

const STEPS = { LOADING: 0, WELCOME: 1, PASSWORD: 2, CREATING: 3, ARRIVED: 4 };

const fade = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
};

export default function JoinFromInvite() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const inviteId = params.get("invite");

  const [step, setStep] = useState(STEPS.LOADING);
  const [invite, setInvite] = useState(null);
  const [fatalError, setFatalError] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  // 1. Validate invite on mount
  useEffect(() => {
    if (!inviteId) {
      setFatalError("Lien d'invitation incomplet. Contactez votre institution.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/institution/invite/${encodeURIComponent(inviteId)}`);
        if (data.status === "accepted") {
          setFatalError("Cette invitation a déjà été utilisée. Connectez-vous plutôt.");
          setInvite(data);
          return;
        }
        setInvite(data);
        setFirstName(data.first_name || "");
        setStep(STEPS.WELCOME);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        setFatalError(typeof detail === "string" ? detail : "Invitation introuvable ou expirée.");
      }
    })();
  }, [inviteId]);

  const canSubmit = useMemo(() => {
    return firstName.trim().length >= 1
      && lastName.trim().length >= 1
      && password.length >= 8
      && password === confirm;
  }, [firstName, lastName, password, confirm]);

  const createAccount = async () => {
    if (!canSubmit || !invite) return;
    setBusy(true);
    setStep(STEPS.CREATING);
    try {
      const { data } = await api.post("/auth/signup", {
        email: invite.email,
        password,
        password_confirm: confirm,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        name: `${firstName.trim()} ${lastName.trim()}`.trim(),
        lang: "fr",
        institution_invite_id: inviteId,
        accept_terms: true,
      });
      setToken(data.access_token);
      await new Promise((r) => setTimeout(r, 400));
      setStep(STEPS.ARRIVED);
      // Emotional pause, then transition into Diagnostic
      setTimeout(() => {
        window.location.href = "/app/diagnostic";
      }, 2200);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Impossible de créer votre compte.";
      toast.error(msg, { duration: 8000 });
      setStep(STEPS.PASSWORD);
    } finally {
      setBusy(false);
    }
  };

  // ---- Rendering ------------------------------------------------------------
  return (
    <div className="min-h-screen bg-[#F8F7F3] text-[#1F2937] relative overflow-hidden">
      {/* Ambient warmth */}
      <div className="pointer-events-none absolute inset-0 opacity-70">
        <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-[#DCC29A]/15 blur-3xl" />
        <div className="absolute bottom-[-30%] right-[-10%] w-[700px] h-[700px] rounded-full bg-[#0B2545]/10 blur-3xl" />
      </div>

      <header className="relative z-10 flex items-center justify-between px-6 md:px-10 py-5">
        <Link to="/" className="flex items-center gap-3" data-testid="join-brand">
          <AltioraTreeLogo size={40} />
          <div className="leading-tight">
            <div className="font-display text-lg tracking-tight text-[#0B2545]">ALTIORA</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.32em] text-[#7A5B14]">COOP</div>
          </div>
        </Link>
        <Link to="/login" data-testid="join-existing-login" className="text-xs font-mono uppercase tracking-[0.2em] text-[#5B5B62] hover:text-[#0B2545] transition-colors">
          Déjà un compte ?
        </Link>
      </header>

      <main className="relative z-10 max-w-2xl mx-auto px-6 md:px-8 pt-10 md:pt-20 pb-24" data-testid="join-from-invite-page">
        <AnimatePresence mode="wait">
          {/* --- LOADING --- */}
          {step === STEPS.LOADING && !fatalError && (
            <motion.div key="loading" {...fade} className="flex flex-col items-center text-center py-24">
              <Loader2 className="h-6 w-6 text-[#0B2545] animate-spin" strokeWidth={1.5} />
              <div className="mt-6 font-mono text-[11px] uppercase tracking-[0.32em] text-[#5B5B62]">
                Validation de votre invitation…
              </div>
            </motion.div>
          )}

          {/* --- FATAL ERROR --- */}
          {fatalError && (
            <motion.div key="error" {...fade} className="pt-16" data-testid="join-invite-error">
              <div className="text-xs font-mono uppercase tracking-[0.32em] text-[#7A5B14]">Invitation ALTIORA</div>
              <h1 className="mt-4 font-display text-3xl md:text-4xl text-[#1F2937] tracking-tight leading-tight">
                Ce lien ne fonctionne pas.
              </h1>
              <p className="mt-4 text-[#5B5B62] leading-relaxed">{fatalError}</p>
              <div className="mt-8 flex gap-3">
                <Link to="/login" className="rounded-md bg-[#0B2545] hover:bg-[#123163] text-white px-5 py-2.5 text-sm font-medium transition-colors">
                  Me connecter
                </Link>
                <Link to="/" className="rounded-md border border-[#E4E1D8] hover:border-[#B8B5AC] px-5 py-2.5 text-sm text-[#1F2937] transition-colors">
                  Retour à l&apos;accueil
                </Link>
              </div>
            </motion.div>
          )}

          {/* --- STEP 1 · WELCOME --- */}
          {step === STEPS.WELCOME && invite && (
            <motion.div key="welcome" {...fade} className="pt-6">
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.6 }}
                className="inline-flex items-center gap-2 rounded-full border border-[#0B2545]/20 bg-white/70 backdrop-blur px-3 py-1.5"
                data-testid="join-invite-org-badge"
              >
                <Building2 className="h-3.5 w-3.5 text-[#0B2545]" strokeWidth={1.5} />
                <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-[#0B2545]">
                  {invite.org_name}
                </div>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.7 }}
                className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1F2937] leading-[1.05]"
              >
                {firstName ? `${firstName}, ` : "Bienvenue. "}
                <br />
                <span className="text-[#0B2545]">
                  {invite.org_name} vous invite<br />sur ALTIORA.
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.55, duration: 0.7 }}
                className="mt-8 text-[15px] md:text-base leading-relaxed text-[#374151] max-w-xl"
              >
                Vous êtes sur le point d&apos;accéder à un espace personnel, confidentiel et
                sécurisé, conçu pour clarifier votre trajectoire économique et
                professionnelle avec l&apos;aide d&apos;une intelligence dédiée.
              </motion.p>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.85, duration: 0.7 }}
                className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg"
              >
                <div className="rounded-lg border border-[#E4E1D8] bg-white/70 backdrop-blur px-4 py-3">
                  <ShieldCheck className="h-4 w-4 text-[#0B2545]" strokeWidth={1.5} />
                  <div className="mt-2 text-xs text-[#374151] leading-snug">
                    Espace personnel confidentiel. Aucun tiers n&apos;y accède sans votre autorisation.
                  </div>
                </div>
                <div className="rounded-lg border border-[#E4E1D8] bg-white/70 backdrop-blur px-4 py-3">
                  <Sparkles className="h-4 w-4 text-[#7A5B14]" strokeWidth={1.5} />
                  <div className="mt-2 text-xs text-[#374151] leading-snug">
                    Diagnostic IMES™ inclus. Un miroir intelligent de votre situation actuelle.
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 0.6 }}
                className="mt-12"
              >
                <button
                  data-testid="join-invite-continue"
                  onClick={() => setStep(STEPS.PASSWORD)}
                  className="group inline-flex items-center gap-3 rounded-md bg-[#0B2545] hover:bg-[#123163] text-white px-6 py-3.5 text-sm font-medium transition-all"
                >
                  Continuer
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
                </button>
                <div className="mt-3 text-[11px] font-mono uppercase tracking-[0.24em] text-[#8B8B8F]">
                  Prend moins d&apos;une minute.
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* --- STEP 2 · PASSWORD --- */}
          {step === STEPS.PASSWORD && invite && (
            <motion.div key="password" {...fade} className="pt-8">
              <div className="text-xs font-mono uppercase tracking-[0.32em] text-[#7A5B14]">
                Étape 1 sur 1 · Créer votre accès
              </div>
              <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight text-[#1F2937] leading-tight">
                Un mot, un secret,<br />et vous êtes chez vous.
              </h2>
              <p className="mt-4 text-[#5B5B62] leading-relaxed max-w-lg">
                Nous avons déjà votre courriel professionnel via <span className="text-[#0B2545] font-medium">{invite.org_name}</span>.
                Choisissez simplement un mot de passe personnel.
              </p>

              <div className="mt-10 space-y-5 max-w-md">
                <div className="rounded-md border border-[#E4E1D8] bg-[#F8F7F3] px-4 py-3">
                  <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#5B5B62]">Votre courriel</div>
                  <div className="mt-1 text-[15px] text-[#1F2937]" data-testid="join-invite-email">{invite.email}</div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-[#5B5B62]">Prénom</div>
                    <input
                      data-testid="join-invite-firstname"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full rounded-md bg-white border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm"
                      autoFocus
                    />
                  </label>
                  <label className="block">
                    <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-[#5B5B62]">Nom</div>
                    <input
                      data-testid="join-invite-lastname"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full rounded-md bg-white border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm"
                    />
                  </label>
                </div>

                <label className="block">
                  <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-[#5B5B62]">Mot de passe (min. 8 caractères)</div>
                  <div className="relative">
                    <input
                      data-testid="join-invite-password"
                      type={showPw ? "text" : "password"}
                      value={password}
                      minLength={8}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full rounded-md bg-white border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 pr-10 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#8B8B8F] hover:text-[#0B2545]"
                      aria-label={showPw ? "Masquer" : "Afficher"}
                    >
                      {showPw ? <EyeOff className="h-4 w-4" strokeWidth={1.5} /> : <Eye className="h-4 w-4" strokeWidth={1.5} />}
                    </button>
                  </div>
                </label>

                <label className="block">
                  <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-[#5B5B62]">Confirmer</div>
                  <input
                    data-testid="join-invite-password-confirm"
                    type={showPw ? "text" : "password"}
                    value={confirm}
                    minLength={8}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full rounded-md bg-white border border-[#E4E1D8] focus:border-[#0B2545] focus:ring-1 focus:ring-[#0B2545] outline-none px-3 py-2.5 text-sm"
                  />
                  {confirm && password !== confirm && (
                    <div className="mt-1 text-[11px] text-red-600">Les mots de passe ne correspondent pas.</div>
                  )}
                </label>

                <div className="pt-2 text-[11px] leading-relaxed text-[#8B8B8F]">
                  En créant votre compte, vous acceptez les
                  {" "}<a href="/terms" target="_blank" rel="noreferrer" className="text-[#0B2545] underline">conditions d&apos;utilisation</a>
                  {" "}et la <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#0B2545] underline">politique de confidentialité</a>.
                  Votre institution ne voit jamais votre mot de passe ni vos réponses au diagnostic.
                </div>

                <button
                  data-testid="join-invite-submit"
                  onClick={createAccount}
                  disabled={!canSubmit || busy}
                  className="group w-full inline-flex items-center justify-center gap-3 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:bg-[#8B8B8F] disabled:cursor-not-allowed text-white px-6 py-3.5 text-sm font-medium transition-all"
                >
                  {busy ? "Création…" : "Ouvrir mon espace ALTIORA"}
                  {!busy && <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />}
                </button>
              </div>
            </motion.div>
          )}

          {/* --- STEP 3 · CREATING --- */}
          {step === STEPS.CREATING && (
            <motion.div key="creating" {...fade} className="flex flex-col items-center text-center py-24">
              <Loader2 className="h-6 w-6 text-[#0B2545] animate-spin" strokeWidth={1.5} />
              <div className="mt-6 font-mono text-[11px] uppercase tracking-[0.32em] text-[#5B5B62]">
                Ouverture de votre espace…
              </div>
              <div className="mt-4 text-[#374151] max-w-sm leading-relaxed">
                Un instant. Nous préparons votre environnement personnel.
              </div>
            </motion.div>
          )}

          {/* --- STEP 4 · ARRIVED (emotional pause) --- */}
          {step === STEPS.ARRIVED && invite && (
            <motion.div key="arrived" {...fade} className="pt-12 md:pt-24">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="flex justify-center"
              >
                <AltioraTreeLogo size={72} />
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.7 }}
                className="mt-8 text-center font-display text-3xl md:text-4xl tracking-tight text-[#1F2937]"
              >
                {firstName ? `Bienvenue, ${firstName}.` : "Bienvenue."}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9, duration: 0.8 }}
                className="mt-4 text-center text-[#5B5B62] leading-relaxed"
              >
                Nous vous emmenons maintenant vers votre premier diagnostic.
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
